<div class="data-grid">
  <app-grid-toolbar
    #toolbar
    [ready]="toolbarReady()"
    [title]="title"
    [icon]="icon"
    [tabs]="toolbarTabs"
    [activeTabId]="currentTabId"
    [tabsLabel]="'grid.toolbar.pinned-view-presets' | translate"
    [tabRemoveLabel]="'grid.toolbar.unpin-view-preset' | translate"
    (tabChange)="handleTabValueChange($event)"
    (tabRemove)="unpinViewPreset($event)"
    [count]="rowCount"
    [loading]="loading()"
    [collectionSize]="paginated ? totalCount() : 0"
    [currentPage]="currentPage()"
    [pageSize]="pageSize"
    (pageChange)="goToPage($event)"
    [showRefresh]="true"
    [showResetFilters]="true"
    [showExportCsv]="csvClientSide"
    [showExportCsvServerSide]="csvServerSide"
    (refresh)="refreshGrid()"
    (resetFilters)="resetGridFilters()"
    (exportCsv)="exportGridAsCsv()"
    (exportCsvServerSide)="exportGridAsCsvServerSide()"
  >
    <!-- Host status region (selection pills), left of the feature actions -->
    <ng-container toolbarStatus>
      <ng-content select="[toolbarStatus]"></ng-content>
    </ng-container>

    <!-- Feature actions: the host view's own buttons, left of the shared controls -->
    <ng-container toolbarActions>
      <ng-content select="[toolbarActions]"></ng-content>
    </ng-container>

    <!-- View presets: combobox, default star, pin, Save dropdown, Presets menu -->
    <ng-container toolbarViewOptions>
      <sui-combobox
        class="grid-view-presets"
        [disabled]="!presetsInitialized()"
        [options]="viewPresetOptions()"
        [clearable]="true"
        placeholder="{{
          'grid.toolbar.placeholder.select-view-preset' | translate
        }}"
        [ngModel]="selectedViewPresetId()"
        (ngModelChange)="onViewPresetValueChange($event)"
      ></sui-combobox>

      <!-- Star and pin write the row's flags: hidden on a locked (System / Company) preset -->
      @if (!selectedViewPreset()?.is_locked) {
        <!-- Default view preset: the grid opens with the starred one -->
        <sui-button
          class="grid-view-preset-default"
          variant="ghost"
          [leadingIcon]="['fas', 'star']"
          [iconOnly]="true"
          [pressed]="selectedViewPreset()?.is_default ?? false"
          [disabled]="!presetsInitialized() || !selectedViewPresetId()"
          [ariaLabel]="'grid.toolbar.default-view-preset' | translate"
          [suiTooltip]="
            (selectedViewPreset()?.is_default
              ? 'grid.toolbar.tooltip.stop-opening-the-grid-with-this-view-preset'
              : 'grid.toolbar.tooltip.open-the-grid-with-this-view-preset-by-default'
            ) | translate
          "
          (click)="toggleSelectedViewPresetDefault()"
        ></sui-button>

        <!-- Pinned view preset: shown as a tab under the grid title -->
        <sui-button
          class="grid-view-preset-pinned"
          variant="ghost"
          [leadingIcon]="['fas', 'thumbtack']"
          [iconOnly]="true"
          [pressed]="selectedViewPreset()?.is_pinned ?? false"
          [disabled]="!presetsInitialized() || !selectedViewPresetId()"
          [ariaLabel]="'grid.toolbar.pin-view-preset' | translate"
          [suiTooltip]="
            (selectedViewPreset()?.is_pinned
              ? 'grid.toolbar.tooltip.unpin-this-view-preset-from-the-tabs'
              : 'grid.toolbar.tooltip.pin-this-view-preset-as-a-tab'
            ) | translate
          "
          (click)="toggleSelectedViewPresetPinned()"
        ></sui-button>
      } @else {
        <!-- Locked preset: a shared System / Company row, read-only for the caller -->
        <sui-button
          class="grid-view-preset-locked"
          variant="ghost"
          [leadingIcon]="['fas', 'lock']"
          [iconOnly]="true"
          [disabled]="true"
          [ariaLabel]="'grid.toolbar.locked-view-preset' | translate"
          [suiTooltip]="
            'grid.toolbar.tooltip.this-shared-view-preset-is-read-only-save-it-as-your-own-to-change-it'
              | translate
          "
        ></sui-button>
      }

      <div class="actions">
        @if (
          presetsInitialized() &&
          (viewSettingsHasChanged() || !selectedViewPresetId())
        ) {
          <!-- Save: always while no preset is selected (Save as new is always valid), otherwise only while the grid differs from the selected preset -->
          <div class="actions-dropdown">
            <sui-button
              variant="primary"
              [leadingIcon]="['fas', 'save']"
              [iconOnly]="toolbar.collapsed()"
              [ariaLabel]="
                toolbar.collapsed() ? ('btn.save' | translate) : undefined
              "
              [trailingIcon]="['fas', 'caret-down']"
              [expanded]="saveDropdown.isOpen()"
              [suiTooltip]="
                'grid.toolbar.tooltip.save-the-changed-grid-settings'
                  | translate
              "
              [suiMenuTrigger]="saveMenu"
              #saveDropdown="suiMenu"
            >
              {{ toolbar.collapsed() ? "" : ("btn.save" | translate) }}
            </sui-button>

            <ng-template #saveMenu>
              <sui-menu>
                @if (selectedViewPreset()?.is_locked) {
                  <!-- A locked preset is never updated in place: the copy becomes the caller's own -->
                  <button sui-menu-item (click)="saveSelectedViewPresetAsNew()">
                    <fa-icon [icon]="['fas', 'copy']"></fa-icon>
                    <span>{{ "btn.save-as-my-preset" | translate }}</span>
                  </button>
                } @else {
                  @if (selectedViewPresetId()) {
                    <button sui-menu-item (click)="saveSelectedViewPreset()">
                      <fa-icon [icon]="['fas', 'save']"></fa-icon>
                      <span>{{ "btn.save-changes" | translate }}</span>
                    </button>
                  }
                  <button sui-menu-item (click)="saveSelectedViewPresetAsNew()">
                    <fa-icon [icon]="['fas', 'copy']"></fa-icon>
                    <span>{{ "btn.save-as-new" | translate }}</span>
                  </button>
                }
              </sui-menu>
            </ng-template>
          </div>
        }

        <div class="actions-dropdown">
          <sui-button
            variant="secondary"
            [leadingIcon]="['fas', 'ellipsis-v']"
            [iconOnly]="toolbar.collapsed()"
            [ariaLabel]="
              toolbar.collapsed() ? ('btn.presets' | translate) : undefined
            "
            [trailingIcon]="['fas', 'caret-down']"
            [expanded]="presetsDropdown.isOpen()"
            [suiTooltip]="
              'grid.toolbar.tooltip.manage-view-presets' | translate
            "
            [disabled]="!presetsInitialized()"
            [suiMenuTrigger]="presetsMenu"
            #presetsDropdown="suiMenu"
          >
            {{ toolbar.collapsed() ? "" : ("btn.presets" | translate) }}
          </sui-button>

          <ng-template #presetsMenu>
            <sui-menu>
              <!-- Create New -->
              <button sui-menu-item (click)="createViewPresetSettings()">
                <fa-icon [icon]="['fas', 'plus']"></fa-icon>
                <span>{{ "btn.create-new" | translate }}</span>
              </button>

              @if (selectedViewPreset()?.is_locked) {
                <!-- Locked (System / Company) preset: no edit or delete, only a copy of one's own -->
                <button sui-menu-item (click)="saveSelectedViewPresetAsNew()">
                  <fa-icon [icon]="['fas', 'copy']"></fa-icon>
                  <span>{{ "btn.save-as-my-preset" | translate }}</span>
                </button>
              } @else {
                <!-- Edit -->
                <button
                  sui-menu-item
                  [disabled]="!selectedViewPresetId()"
                  (click)="editSelectedViewPreset()"
                >
                  <fa-icon [icon]="['fas', 'edit']"></fa-icon>
                  <span>{{ "btn.edit" | translate }}</span>
                </button>

                <!-- Delete -->
                <button
                  sui-menu-item
                  [disabled]="!selectedViewPresetId()"
                  (click)="deleteSelectedGridSettings()"
                >
                  <fa-icon [icon]="['fas', 'trash']"></fa-icon>
                  <span>{{ "btn.delete" | translate }}</span>
                </button>
              }

              <!-- Shared presets (WAP-1631 item 7): Company actions for Client Admin+, System actions for Platform Admin+ -->
              @if (canShareWithCompany()) {
                <sui-menu-separator></sui-menu-separator>
                <sui-menu-header>
                  {{ "grid.toolbar.menu-header.shared-presets" | translate }}
                </sui-menu-header>

                <button
                  sui-menu-item
                  (click)="saveAsSharedViewPreset(GridViewPresetScope.COMPANY)"
                >
                  <fa-icon [icon]="['fas', 'building']"></fa-icon>
                  <span>{{ "btn.save-as-company-preset" | translate }}</span>
                </button>
                @if (canShareWithSystem()) {
                  <button
                    sui-menu-item
                    (click)="saveAsSharedViewPreset(GridViewPresetScope.SYSTEM)"
                  >
                    <fa-icon [icon]="['fas', 'globe']"></fa-icon>
                    <span>{{ "btn.save-as-system-preset" | translate }}</span>
                  </button>
                }

                @if (selectedViewPreset() && !selectedViewPreset()?.is_locked) {
                  <button
                    sui-menu-item
                    (click)="
                      promoteSelectedViewPreset(GridViewPresetScope.COMPANY)
                    "
                  >
                    <fa-icon [icon]="['fas', 'level-up-alt']"></fa-icon>
                    <span>{{
                      "btn.promote-to-company-preset" | translate
                    }}</span>
                  </button>
                  @if (canShareWithSystem()) {
                    <button
                      sui-menu-item
                      (click)="
                        promoteSelectedViewPreset(GridViewPresetScope.SYSTEM)
                      "
                    >
                      <fa-icon [icon]="['fas', 'level-up-alt']"></fa-icon>
                      <span>{{
                        "btn.promote-to-system-preset" | translate
                      }}</span>
                    </button>
                  }
                }
              }
            </sui-menu>
          </ng-template>
        </div>
      </div>
    </ng-container>
  </app-grid-toolbar>

  <!-- Host row between the toolbar and the grid (quick search, filter chips) -->
  <ng-content select="[belowToolbar]"></ng-content>

  <!-- Main area: grid + optional host side region (data view / detail panel) -->
  <div class="content">
    <div
      #dataTable
      class="{{ 'data-table' + (gridClassName ? ' ' + gridClassName : '') }}"
      [class.data-table--master-detail]="trackRowSelection"
    >
      @if (!objectEditorMode) {
        <ag-grid-angular
          style="width: 100%; height: 100%"
          class="ag-theme-sensika"
          [gridOptions]="gridOptions"
          [cellSelection]="true"
          (rowClicked)="onObjectSelected($event)"
          (cellValueChanged)="onCellValueChanged($event)"
          (displayedColumnsChanged)="onDisplayedColumnsChanged()"
          (toolPanelVisibleChanged)="onDisplayedColumnsChanged()"
        >
        </ag-grid-angular>
      } @else {
        <router-outlet></router-outlet>
      }
    </div>

    <ng-content select="[sideRegion]"></ng-content>
  </div>
</div>
